import './PlaylistsView.scss';
import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import PlaylistList from '../../components/playlistList/PlaylistList';
import PlaylistGroupList from '../../components/playlistGroupList/PlaylistGroupList'
import { useTranslation } from 'react-i18next';
import { SORT_PLAYLIST_OPTIONS, GLOBAL_COUNTRY_IRI, RESPONSE, type PlaylistOrderBy, RolesOptions } from '../../js/_constants';
import CreatePlaylistModal from '../../components/createPlaylistModal/CreatePlaylistModal';
import CreatePlaylistGroupModal from '../../components/createPlaylistGroupModal/CreatePlaylistGroupModal';
import { userStore, searchStore, generalStore } from '../../store';
import { isNotEmpty } from '../../js/fpUtils';
import IconButton from '../../components/iconButton/IconButton';
import FilterTags from '../../components/filterTags/FilterTags';
import plusIconPath from '@icons/add-small.svg';
import { type Playlist, type PlaylistGroup } from '../../api/types';
import { equals, prepend} from 'ramda';
import Tabs from '../../components/tabs/Tabs';
import Loader from '../../components/loader/Loader';
import Paginator from '../../components/paginator/Paginator';
import _server from '../../js/config/_server';
import LayoutSwitcher from '../../components/sortToggle/SortToggle';
import Dropdown from '../../components/dropdown/Dropdown';
import { useLocation, useNavigate } from 'react-router';
import queryString from 'query-string';
import { type ParsedQuery } from 'query-string';
import { BRAND_LETTER } from '../../js/config/_global';
import MultiSelectDropdown from '../../components/multiSelectDropdown/MultiSelectDropdown';

interface FilterTag {
    key: number;
    title: string;
  }

const PlaylistsView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { details, loading } = userStore();
    const { gridStyle } = generalStore();
    const { setSearchQuery  } = searchStore();

    const FALLBACK_KEY_GLOBAL = t('filter_fallback_global')
    const FALLBACK_KEY_MARKETS = t('filter_fallback_markets')

    const [activeTab, setActiveTab] = useState(0);

    const playlistContainerRef = useRef<HTMLDivElement>(null);

    const [isCreatePlaylistModalOpen, setIsCreatePlaylistModalOpen] = useState(false);

    const [isCreatePlaylistGroupModalOpen, setIsCreatePlaylistGroupModalOpen] = useState(false);

    const [urlSearchParams, setUrlSearchParams] = useState<ParsedQuery<string | number>>();

    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const [isFallbackGlobal, setFallbackGlobal] = useState(false);

    const [filterTags, setFilterTags] = useState<FilterTag[]>([]);

    const [isFallbackCountries, setFallbackCountries] = useState(false);

    const [fallbackList, setFallbackList] = useState<string[]>([]);

    const [countriesList, setCountriesList] = useState<string[]>([]);

    const [roleList, setRoleList] = useState<string[]>([]);

    const currentSearchParams = useRef('');

    useEffect(() => {
        if (!equals(location.search, currentSearchParams.current)) {
            setUrlSearchParams(
                queryString.parse(location.search, { parseNumbers: true })
            );
        }
        currentSearchParams.current = location.search;
    }, [location]);

    const globalOption = {
        value: GLOBAL_COUNTRY_IRI,
        label: 'Global'
    };

    const createFallbackOptions = affiliatedCountries => {
        const options = affiliatedCountries.map(country => ({
            value: country[RESPONSE.ID],
            label: country.name
        }));
        return prepend(globalOption, options)
    }

    const createCountryOptions = affiliatedCountries => {
        const options = affiliatedCountries.map(country => ({
            value: country[RESPONSE.ID],
            label: country.name
        }));
        return options
    }
   
    const [fallbackOptions, setFallbackOptions] = useState(createFallbackOptions(details.affiliatedCountries));

    const [countriesOptions, setCountriesOptions] = useState(createCountryOptions(details.affiliatedCountries));

    const removeFilter = (name, index, filterKey) => {
        if (name === FALLBACK_KEY_GLOBAL) {
            handleFilterClick(FALLBACK_KEY_GLOBAL)
        }

        if (name === FALLBACK_KEY_MARKETS) {
            handleFilterClick(FALLBACK_KEY_MARKETS)
        }
    };

    const openModal = () => {
        activeTab === 2 ? setIsCreatePlaylistGroupModalOpen(true) : setIsCreatePlaylistModalOpen(true);
    };

    const onCloseModalClick = () => {
        activeTab === 2 ? setIsCreatePlaylistGroupModalOpen(false) : setIsCreatePlaylistModalOpen(false);
    };

    const [updateList, setUpdateList] = useState(false);

    const handleSuccessfulPlaylistCreation = event => {
        if (event?.state !== 'redirecting') {
            setIsCreatePlaylistModalOpen(false);
            setTimeout(() => {
                setUpdateList(!updateList);
            }, 1000);
        }
    };

    const handleSuccessfulPlaylistGroupCreation = event => {
        if (event?.state !== 'redirecting') {
            setIsCreatePlaylistGroupModalOpen(false);
            setTimeout(() => {
                handleResetResults();
                setUpdateList(!updateList);
            }, 1000);
        }
    };

    const [sortParam, setSortParam] = useState<PlaylistOrderBy>(
        SORT_PLAYLIST_OPTIONS[0].query
    );

    const handleSortChange = (e, name, value, index: number) => {
        const option = SORT_PLAYLIST_OPTIONS[index];
        setSortParam(option.query);
    };

    if (loading) return <Loader />;

    const renderFilterTags = () => {
        if (isNotEmpty(filterTags)) {
            return (
                <FilterTags
                    key="filter-tags"
                    tags={filterTags}
                    classes="is-playlist-view"
                    onRemoveFilter={removeFilter}
                />
            );
        }

        return '';
    };

    const handleFilterAdd = (newFilter) => {
        setFilterTags(prevFilters => [
        ...prevFilters,
        newFilter
        ]);
    };

    const handleFilterRemove = (keyToRemove) => {
        setFilterTags(prevItems =>
            prevItems.filter(item => item.key !== keyToRemove)
        );
    };

    const handleFilterClick = (fallbackKey) => {
        if (fallbackKey === FALLBACK_KEY_GLOBAL) {
            setFallbackGlobal(!isFallbackGlobal)
        }

        if (fallbackKey === FALLBACK_KEY_MARKETS) {
            setFallbackCountries(!isFallbackCountries)         
        }
    }

    const onSelectFallback = selectedOptions => {
        setFallbackList(selectedOptions.map(fallback => fallback.value.split('/').pop()));
    };

    const onSelectCountry = selectedOptions => {
        setCountriesList(selectedOptions.map(country => country.value.split('/').pop()));
    }
    
    const onSelectRole= selectedOptions => {
        setRoleList(selectedOptions.map(role => role.value));
    };

    const renderFilters = () => {
        return (
            <div className={classNames('b-filter-area-container', 
            {'hidden': !isFilterOpen})}>
                <MultiSelectDropdown
                    className="fallback-dropdown"
                    name="playlist_fallback_for"
                    label={t('playlist_fallback_for')}
                    options={fallbackOptions}
                    onChange={onSelectFallback}
                    isFilterable
                />
                <MultiSelectDropdown
                    className="country-dropdown"
                    name="playlist_shared_with"
                    label={t('playlist_shared_with')}
                    options={countriesOptions}
                    onChange={onSelectCountry}
                    isFilterable
                />
                <MultiSelectDropdown
                    className="roles-dropdown"
                    name="playlist_role"
                    label={t('playlist_system_roles')}
                    options={RolesOptions}
                    onChange={onSelectRole}
                    isFilterable
                    withSelectAllButton
                />  
            </div>
        );
    };

    const toggleFilter = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    const handleResetResults = () => {
        setSearchQuery('');
        navigate(location.pathname);
    };

    const onSelectTab = (tab) => {
        setActiveTab(tab);
        tab === 2 && setIsFilterOpen(false);
    }
    
    return (
        <div className="b-playlists-view" ref={playlistContainerRef}>
            <div className="filters-container">
                <IconButton
                    className="b-playlists-view__create-playlist-button"
                    onClick={openModal}
                    src={plusIconPath}
                    name='create new playlist'
                />
                {activeTab !== 2 && <IconButton
                    icon='filter'
                    onClick={toggleFilter}
                    size='small'
                    name='filter icon'
                    className='is-playlist-icon-filter'
                />}
                <LayoutSwitcher />
                {activeTab !== 2 && <Dropdown
                    name="sorting"
                    label={t('sort_by')}
                    options={SORT_PLAYLIST_OPTIONS.map(option =>
                        t(option.translationKey)
                    )}
                    onSelect={handleSortChange}
                    preselection={0}
                />}
            </div>
            {renderFilters()}
            <Tabs activeTabIndex={location.state?.activeTab} onSelect={onSelectTab}>
                <Paginator<Playlist>
                    source={_server.services.playlists.userPlaylists}
                    data-tab-label={t('my_playlists')}
                    preFetch
                    scrollIntoViewElement={playlistContainerRef.current}
                    params={{
                        brand: BRAND_LETTER,
                        createdByCurrentUser: true,
                        fallbackCountries: fallbackList,
                        countries: countriesList,
                        creatorRole: roleList,
                        ...urlSearchParams,
                        ...sortParam
                    }}
                    updateList={updateList}
                >
                    {(paginatedPlaylists, totalItems) => (
                        <PlaylistList
                            gridStyle={gridStyle}
                            label={t('my_playlists')}
                            playlists={paginatedPlaylists}
                            totalItems={totalItems}
                            tags={urlSearchParams && 'title' in urlSearchParams && 
                                <FilterTags
                                    key="filter-tags"
                                    tags={[{key:urlSearchParams.title, title: urlSearchParams.title}]}
                                    classes="is-playlist-view"
                                    onRemoveFilter={handleResetResults}
                                />}
                        />                            
                    )}
                </Paginator>
                <Paginator<Playlist>
                    data-tab-label={t('market_playlists')}
                    source={_server.services.playlists.userPlaylists}
                    scrollIntoViewElement={playlistContainerRef.current}
                    preFetch
                    params={{
                        brand: BRAND_LETTER,
                        createdByCurrentUser: false,
                        fallbackCountries: fallbackList,
                        countries: countriesList,
                        creatorRole: roleList,
                        ...urlSearchParams,
                        ...sortParam
                    }}
                    updateList={updateList}
                >
                    {(paginatedPlaylists, totalItems) => (
                        <PlaylistList
                            gridStyle={gridStyle}
                            label={t('market_playlists')}
                            playlists={paginatedPlaylists}
                            totalItems={totalItems}
                            tags={urlSearchParams && 'title' in urlSearchParams && 
                                <FilterTags
                                    key="filter-tags"
                                    tags={[{key:urlSearchParams.title, title: urlSearchParams.title}]}
                                    classes="is-playlist-view"
                                    onRemoveFilter={handleResetResults}
                                />}
                        />
                    )}
                </Paginator>
                <Paginator<PlaylistGroup>
                    data-tab-label={t('playlist_groups')}
                    source={_server.services.playlistGroups.getAll}
                    scrollIntoViewElement={playlistContainerRef.current}
                    preFetch
                    params={{
                        ...(urlSearchParams?.title && { title: String(urlSearchParams.title) }),
                    }}
                    updateList={updateList}
            >
                {(paginatedPlaylistGroups, totalItems) => (
                    <PlaylistGroupList
                        gridStyle={gridStyle}
                        label={t('playlist_groups')}
                        playlistGroups={paginatedPlaylistGroups}
                        totalItems={totalItems}
                        tags={urlSearchParams && 'title' in urlSearchParams && 
                            <FilterTags
                                key="filter-tags"
                                tags={[{key:urlSearchParams.title, title: urlSearchParams.title}]}
                                classes="is-playlist-view"
                                onRemoveFilter={handleResetResults}
                            />}
                    />
                )}
            </Paginator>
            </Tabs>
            {isCreatePlaylistModalOpen && (
                <CreatePlaylistModal
                    isOpen={isCreatePlaylistModalOpen}
                    onSuccess={handleSuccessfulPlaylistCreation}
                    onClose={onCloseModalClick}
                />
            )}
            {isCreatePlaylistGroupModalOpen && (
                <CreatePlaylistGroupModal
                    isOpen={isCreatePlaylistGroupModalOpen}
                    onSuccess={handleSuccessfulPlaylistGroupCreation}
                    onClose={onCloseModalClick}
                />
            )}
        </div>
    );
};

export default PlaylistsView;


PlaylistsView.whyDidYouRender = {
    logOnDifferentValues: true,
    customName: 'PlaylistsView'
};
